import { AlarmClockIcon, CheckCircle2Icon, ClockIcon, TimerOffIcon } from 'lucide-react';
import type { SecurityIncident } from '@kennisbank/shared';
import { formatDuur, formatMoment, termijnStatus } from './termijn.js';

/**
 * De klok, in één chip. Dit is de reden dat het beheerscherm bestaat: de
 * 72-uurstermijn richting de AP moet zichtbaar zijn zonder dat iemand een
 * detailpagina hoeft te openen.
 *
 * De countdown verschijnt uitsluitend bij `triage.isDatalek === true`. Zolang
 * er geen triage is toont de badge hoelang de melding al wacht, want dát is
 * dan de termijn die loopt.
 */
export function TermijnBadge({
  incident,
  nu,
  compact = false,
}: {
  incident: SecurityIncident;
  nu: number;
  compact?: boolean;
}): JSX.Element {
  const status = termijnStatus(incident, nu);
  const base = compact
    ? 'inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-[11px] font-semibold'
    : 'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold';
  const iconSize = compact ? 12 : 16;

  switch (status.soort) {
    case 'overschreden':
      return (
        <span
          className={`${base} bg-error text-white`}
          title={`Termijn verlopen op ${formatMoment(new Date(status.deadline).toISOString())}. Nog niet gemeld aan de Autoriteit Persoonsgegevens.`}
        >
          <TimerOffIcon size={iconSize} />
          AP-termijn overschreden, {formatDuur(status.overtijdMs)} te laat
        </span>
      );
    case 'loopt':
      return (
        <span
          className={
            status.urgent
              ? `${base} bg-error/10 text-error ring-1 ring-error/40`
              : `${base} bg-mosterdgeel-10 text-ring1-fg ring-1 ring-ring1-border`
          }
          title={`Melden aan de Autoriteit Persoonsgegevens vóór ${formatMoment(new Date(status.deadline).toISOString())}.`}
        >
          <AlarmClockIcon size={iconSize} />
          Nog {formatDuur(status.resterendMs)} tot de AP-termijn
        </span>
      );
    case 'ap_gemeld':
      return (
        <span className={`${base} bg-success/10 text-success ring-1 ring-success/30`}>
          <CheckCircle2Icon size={iconSize} />
          Gemeld aan de AP op {formatMoment(status.op)}
          {status.opTijd === false ? ' (na de termijn)' : ''}
        </span>
      );
    case 'geen_meldplicht':
      return (
        <span className={`${base} bg-ink-50 text-ink-900/70 ring-1 ring-ink-100`}>
          <CheckCircle2Icon size={iconSize} />
          Datalek, gemotiveerd niet meldplichtig
        </span>
      );
    case 'geen_datalek':
      return (
        <span className={`${base} bg-ink-50 text-ink-900/70 ring-1 ring-ink-100`}>
          <CheckCircle2Icon size={iconSize} />
          Beoordeeld: geen datalek
        </span>
      );
    case 'afgerond':
      return (
        <span className={`${base} bg-ink-50 text-ink-900/70 ring-1 ring-ink-100`}>
          <CheckCircle2Icon size={iconSize} />
          Afgerond zonder kwalificatie
        </span>
      );
    case 'wacht_op_triage':
      return (
        <span
          className={
            status.teLaat
              ? `${base} bg-mosterdgeel-10 text-ring1-fg ring-1 ring-ring1-border`
              : `${base} bg-petrol-50 text-petrol-700`
          }
          title="De 72-uursklok richting de AP begint pas als je deze melding als datalek kwalificeert."
        >
          <ClockIcon size={iconSize} />
          {status.teLaat ? 'Wacht al ' : 'Wacht '}
          {formatDuur(status.wachtMs)} op triage
        </span>
      );
    default:
      return (
        <span className={`${base} bg-ink-50 text-ink-900/60 ring-1 ring-ink-100`}>
          <ClockIcon size={iconSize} />
          Termijn onbekend
        </span>
      );
  }
}
